import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Portal, Tooltip } from '@mui/material';
import { Autocomplete } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useMutation } from 'hooks/apollo/useMutation';
import FlexBox from 'components/common/FlexBox';
import ErrorIcon from '@mui/icons-material/Error';
import useQuery from 'hooks/apollo/useQuery';
import useSubscription from 'hooks/apollo/useSubscription';
import { isIncomeSchemaEditableVar } from '../variables';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';
import { LEASING_INCOME_SCHEMA_NEXT_STATES, LEASING_INCOME_SCHEMA_STATE } from 'features/leasingIncomeSchema/queries';
import { UPDATE_LEASING_INCOME_SCHEMA_STATE } from 'features/leasingIncomeSchema/mutations';
import { LEASING_INCOME_SCHEMA_STATE_UPDATED } from 'features/leasingIncomeSchema/subscriptions';

const LeasingIncomeSchemaStateContainer = ({ id }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);

  const [initialState, setInitialState] = useState();
  const [currentState, setCurrentState] = useState();

  const [error, setError] = useState();
  const [pending, setPending] = useState(false);

  // #region Load

  useQuery(LEASING_INCOME_SCHEMA_STATE, {
    variables: { id },
    skip: !id,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      setCurrentState(data?.leasingIncomeSchemaState);
      setInitialState(data?.leasingIncomeSchemaState);
      if (!data?.leasingIncomeSchemaState?.flowId) setError(t('LeasingIncomeSchema.NoFlow'));
      else if (!data?.leasingIncomeSchemaState?.id) setError(t('LeasingIncomeSchema.NoState'));
    }
  });

  const { data: nextStatesData } = useQuery(LEASING_INCOME_SCHEMA_NEXT_STATES, {
    variables: { stateId: initialState?.id },
    skip: !initialState?.id
  });

  const nextStates = useMemo(() => nextStatesData?.leasingIncomeSchemaNextStates || [], [nextStatesData]);

  // #endregion

  // #region Save

  const [updateLeasingIncomeSchemaState] = useMutation(UPDATE_LEASING_INCOME_SCHEMA_STATE, {
    onError: _ => {
      setPending(false);
      setCurrentState(initialState);
    }
  });

  const handleUpdate = useCallback(
    value => {
      setPending(true);
      setCurrentState(value);
      updateLeasingIncomeSchemaState({
        variables: { id, stateId: value.id }
      });
    },
    [id, updateLeasingIncomeSchemaState]
  );

  useSubscription(LEASING_INCOME_SCHEMA_STATE_UPDATED, {
    onError: error => addToast(error, 'error'),
    skip: !id,
    variables: { id },
    onData: ({ client, data }) => {
      setPending(false);
      setInitialState(currentState);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingIncomeSchemas' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingIncomeSchemaState', args: { id } });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'leasingIncomeSchemaStateHistory', args: { id } });
      addToast(t('LeasingIncomeSchema.StateChanged'), 'success');
      isIncomeSchemaEditableVar(data.data.leasingIncomeSchemaStateUpdated.leasingIncomeSchema.isEditable);
    }
  });

  // #endregion

  return (
    <Portal container={() => document.getElementById('leasing-income-schema-state')}>
      <FlexBox alignItems='end' gap={0.5}>
        <Autocomplete
          label={t('LeasingIncomeSchema.State')}
          value={currentState}
          onChange={handleUpdate}
          options={nextStates}
          disabled={!canWrite || pending || error}
        />
        {error && (
          <Tooltip title={error}>
            <ErrorIcon color='error' fontSize='small' sx={{ mb: 0.5 }} />
          </Tooltip>
        )}
      </FlexBox>
    </Portal>
  );
};

LeasingIncomeSchemaStateContainer.propTypes = {
  id: PropTypes.number
};

export default LeasingIncomeSchemaStateContainer;
